import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppSettings } from '../../types';

const defaultSettings: AppSettings = {
  numberOfClocks: 1,
  clockProfileIds: [],
  clockThemeId: 'playful_focus',
  showPastTasks: true,
  showMinuteTicks: false,
  taskSortingMode: 'default',
  testMode: false,
};

const KEYS = {
  THEME: 'myminu_theme',
  NUM_CLOCKS: 'myminu_num_clocks',
  CLOCK_PROFILES: 'myminu_profiles_map',
  SHOW_TICKS: 'myminu_show_ticks',
  TEST_MODE: 'myminu_test_mode',
  TEST_PREVIEW_MINUTES: 'myminu_test_preview_minutes',
};

function readSettings(): AppSettings {
  const theme = localStorage.getItem(KEYS.THEME);
  const numClocks = localStorage.getItem(KEYS.NUM_CLOCKS);
  const profiles = localStorage.getItem(KEYS.CLOCK_PROFILES);
  const showTicks = localStorage.getItem(KEYS.SHOW_TICKS);
  const testMode = localStorage.getItem(KEYS.TEST_MODE);
  const testPreviewMinutes = localStorage.getItem(KEYS.TEST_PREVIEW_MINUTES);

  return {
    ...defaultSettings,
    clockThemeId: theme || defaultSettings.clockThemeId,
    numberOfClocks: numClocks ? parseInt(numClocks, 10) : defaultSettings.numberOfClocks,
    clockProfileIds: profiles ? JSON.parse(profiles) : defaultSettings.clockProfileIds,
    showMinuteTicks: showTicks ? JSON.parse(showTicks) : defaultSettings.showMinuteTicks,
    testMode: testMode ? JSON.parse(testMode) : defaultSettings.testMode,
    testPreviewMinutes: testPreviewMinutes != null ? parseInt(testPreviewMinutes, 10) : undefined,
  };
}

export function useSettings() {
  const [tick, setTick] = useState(0);

  const settings = useMemo(readSettings, [tick]);

  const updateSettings = useCallback(async (next: Partial<AppSettings>) => {
    if (next.clockThemeId !== undefined) localStorage.setItem(KEYS.THEME, next.clockThemeId);
    if (next.numberOfClocks !== undefined) localStorage.setItem(KEYS.NUM_CLOCKS, String(next.numberOfClocks));
    if (next.clockProfileIds !== undefined) localStorage.setItem(KEYS.CLOCK_PROFILES, JSON.stringify(next.clockProfileIds));
    if (next.showMinuteTicks !== undefined) localStorage.setItem(KEYS.SHOW_TICKS, JSON.stringify(next.showMinuteTicks));
    if (next.testMode !== undefined) localStorage.setItem(KEYS.TEST_MODE, JSON.stringify(next.testMode));
    if (next.testPreviewMinutes !== undefined) localStorage.setItem(KEYS.TEST_PREVIEW_MINUTES, String(next.testPreviewMinutes));
    setTick(t => t + 1);
  }, []);

  useEffect(() => {
    const onStorage = () => setTick(t => t + 1);
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return {
    settings,
    loading: false,
    error: null,
    updateSettings,
  };
}
